import { DatePickerInput, Flex, InputWrap, SegmentedControl } from '@libs/ui/ds'
import { useComponentsTranslation } from '@libs/utils/translations'
import withController from '@spa/hoc/withController'
import type { TimeoffRequestFormType } from '@spa/scenes/MemberPage/modals/TimeoffRequestFormModal/TimeoffRequestFormModal'
import type { Moment } from 'moment'
import moment from 'moment'
import type { FC } from 'react'
import { useFormContext } from 'react-hook-form'

type DoubleStringTuple = [string, string]

export const MORNING = 'morning'
export const AFTERNOON = 'afternoon'

export const halfDay = [MORNING, AFTERNOON] as const

export type HalfDay = (typeof halfDay)[number]
export type DateRangeItemValue = {
  halfDay: HalfDay
  date?: string | Moment
}

export type DateRangeValue = {
  start: DateRangeItemValue
  end: DateRangeItemValue
}

export type DateRangeProps = {
  restIsLocked?: boolean
  inputName: string
  placeholders?: DoubleStringTuple
  disableCustomRest?: boolean
  value?: DateRangeValue
  minDate?: string // MUST BE YYYY-MM-DD
  onChange?(val: DateRangeValue): void
  halfDayAvailable: boolean
  dataTestId?: string
}

export const FieldDateRange: FC<DateRangeProps> = ({
  value = {
    start: { halfDay: MORNING, date: undefined },
    end: { halfDay: MORNING, date: undefined }
  },
  restIsLocked = false,
  disableCustomRest = false,
  minDate = undefined,
  onChange,
  inputName,
  placeholders = [],
  halfDayAvailable = true,
  dataTestId
}) => {
  const { start, end } = value
  const { t } = useComponentsTranslation()

  const {
    formState: { errors }
  } = useFormContext<TimeoffRequestFormType>()

  const endsHalfIsAfternoon = end.halfDay === AFTERNOON
  const startsHalfIsAfternoon = start.halfDay === AFTERNOON
  const startsDateIsSameOrAfter = moment(start.date).isSameOrAfter(end.date)

  const isEndHalfDayDisabled = () => {
    const startsDateIsSameOrAfter = moment(start.date).isSameOrAfter(end.date)
    return (
      startsDateIsSameOrAfter && endsHalfIsAfternoon && startsHalfIsAfternoon
    )
  }

  /**
   *
   */
  const handleChange = (val: DateRangeValue) => {
    if (onChange) {
      onChange(val)
    }
  }

  /**
   * Update start.date
   * Keep [end_date] and [end_half_day] in sync to prevent errors
   */
  const handleStartDayChange = (val: string) => {
    const startsDateIsSameOrAfter = moment(val).isSameOrAfter(end.date)

    return handleChange({
      ...value,
      start: { ...start, date: val },
      ...(startsDateIsSameOrAfter ? { end: { ...end, date: val } } : {})
    })
  }

  /**
   * Update start.halfDay
   * Keep [end_date] synchronized as well as [end_half_day]
   */
  const handleStartHalfDayChange = (val: HalfDay) => {
    const startsHalfIsAfternoon = val === AFTERNOON
    const halfDaysNeedSync =
      startsDateIsSameOrAfter && startsHalfIsAfternoon && !endsHalfIsAfternoon

    return handleChange({
      ...value,
      start: { ...start, halfDay: val },
      ...(halfDaysNeedSync ? { end: { ...end, halfDay: val } } : {})
    })
  }

  /**
   * Update end.date
   */
  const handleEndDayChange = (val: string) =>
    handleChange({ ...value, end: { ...end, date: val } })

  /**
   * Update end.halfDay
   */
  const handleEndHalfDayChange = (val: HalfDay) =>
    handleChange({ ...value, end: { ...end, halfDay: val } })

  return (
    <Flex className='gap-4'>
      {['start', 'end'].map((key, i) => {
        const isStart = i === 0

        const formattedStartDate = start.date
          ? moment(start.date).format('YYYY-MM-DD')
          : undefined

        const formattedEndDate = end.date
          ? moment(end.date).format('YYYY-MM-DD')
          : undefined

        const formattedMinDate = minDate
          ? moment(minDate).format('YYYY-MM-DD')
          : undefined

        return (
          <div key={key} data-testid={dataTestId && `${dataTestId}-${key}`}>
            <InputWrap
              field={
                <Flex row className='justify-between gap-3'>
                  <div className='flex-[2]'>
                    <DatePickerInput
                      key={isStart ? `${inputName}.start` : `${inputName}.end`}
                      value={isStart ? formattedStartDate : formattedEndDate}
                      onChange={
                        isStart ? handleStartDayChange : handleEndDayChange
                      }
                      disabled={restIsLocked || disableCustomRest}
                      placeholder={placeholders[i]}
                      minDate={isStart ? formattedMinDate : formattedStartDate}
                      errorMessage={
                        isStart
                          ? errors?.timeRange?.start?.date?.message
                          : errors?.timeRange?.end?.date?.message
                      }
                    />
                  </div>
                  {halfDayAvailable && (
                    <div className='flex-1'>
                      <SegmentedControl
                        onChange={
                          isStart
                            ? handleStartHalfDayChange
                            : handleEndHalfDayChange
                        }
                        value={isStart ? start.halfDay : end.halfDay}
                        data={[
                          {
                            label: t('components.fieldDateRange.morning'),
                            value: 'morning',
                            ...(!isStart && {
                              disabled: isEndHalfDayDisabled()
                            })
                          },
                          {
                            label: t('components.fieldDateRange.afternoon'),
                            value: 'afternoon'
                          }
                        ]}
                        disabled={restIsLocked || disableCustomRest}
                      />
                    </div>
                  )}
                </Flex>
              }
              label={
                isStart
                  ? t('components.fieldDateRange.from')
                  : t('components.fieldDateRange.to')
              }
            />
          </div>
        )
      })}
    </Flex>
  )
}

export const ControlledFieldDateRange = withController<
  DateRangeProps,
  DateRangeValue
>()(FieldDateRange)
